import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, MapPin } from "lucide-react";
import { Panel, StatusBadge } from "@/components/admin/ui";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { LocationPreview } from "@/components/org/settings/LocationPreview";
import { COUNTRIES, STATES_BY_COUNTRY } from "@/lib/geoData";

const inputClass = "rounded-xl border border-slate-300 px-3 py-2 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white";
const labelClass = "text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500";

export const BusinessLocationsPanel = () => {
  const { API } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("Branch");
  const [creating, setCreating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [addressDraft, setAddressDraft] = useState({});
  const [verifyingId, setVerifyingId] = useState(null);
  const [verifyStatus, setVerifyStatus] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const { data: resp } = await axios.get(`${API}/org/locations`, { withCredentials: true });
      setData(resp);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <Panel title="Additional Business Locations" testId="org-locations-panel">{error && <p className="text-sm font-semibold text-red-600 dark:text-red-400">{error}</p>}</Panel>;
  }

  const { locations, is_manager: isManager } = data;
  const hq = locations.find((l) => l.is_headquarters);
  const additional = locations.filter((l) => !l.is_headquarters);

  const createLocation = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setError("");
    try {
      await axios.post(`${API}/org/locations`, { name: newName.trim(), location_type: newType }, { withCredentials: true });
      setNewName("");
      setNewType("Branch");
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const toggleStatus = async (loc) => {
    setSavingId(loc.location_id);
    try {
      await axios.patch(`${API}/org/locations/${loc.location_id}`, { status: loc.status === "Active" ? "Inactive" : "Active" }, { withCredentials: true });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  const openManage = (loc) => {
    if (expandedId === loc.location_id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(loc.location_id);
    setVerifyStatus(null);
    setAddressDraft({
      street: loc.address?.street || "", line2: loc.address?.line2 || "", city: loc.address?.city || "", state: loc.address?.state || "",
      zip: loc.address?.zip || "", country: loc.address?.country || "US",
    });
  };

  const verifyLocation = async (locationId) => {
    setVerifyingId(locationId);
    setVerifyStatus(null);
    try {
      const { data: resp } = await axios.post(`${API}/org/locations/${locationId}/verify-address`, addressDraft, { withCredentials: true });
      setVerifyStatus(resp);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setVerifyingId(null);
    }
  };

  const identifyLocation = async (locationId, lat, lng) => {
    try {
      await axios.post(`${API}/org/locations/${locationId}/identify-location`, { lat, lng }, { withCredentials: true });
      setVerifyStatus(null);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <Panel
      title="Additional Business Locations"
      subtitle="Informational business addresses — branches, offices and service locations. Not connected to Service Areas or Teams."
      testId="org-locations-panel"
      action={isManager ? undefined : <span className="text-xs font-semibold text-slate-400">View only</span>}
    >
      {error && <p className="mb-3 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="org-locations-error">{error}</p>}

      {hq && (
        <div className="mb-3 flex items-center justify-between rounded-xl border border-slate-200 p-3 dark:border-white/10" data-testid="org-location-hq">
          <div>
            <p className="text-sm font-bold text-slate-950 dark:text-white">{hq.name} <span className="ml-1 text-xs font-semibold text-slate-400">(Headquarters)</span></p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Managed above via Business Location.</p>
          </div>
          <StatusBadge status="Active" testId="org-location-hq-status" />
        </div>
      )}

      <div className="space-y-2" data-testid="org-locations-list">
        {additional.map((loc) => (
          <div key={loc.location_id} className="rounded-xl border border-slate-200 p-3 dark:border-white/10" data-testid={`org-location-${loc.location_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-bold text-slate-950 dark:text-white">{loc.name}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{loc.location_type}</p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={loc.status} testId={`org-location-status-${loc.location_id}`} />
                {isManager && (
                  <>
                    <button type="button" onClick={() => toggleStatus(loc)} disabled={savingId === loc.location_id} data-testid={`org-location-toggle-status-${loc.location_id}`} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10">
                      {loc.status === "Active" ? "Deactivate" : "Activate"}
                    </button>
                    <button type="button" onClick={() => openManage(loc)} data-testid={`org-location-manage-${loc.location_id}`} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10">
                      {expandedId === loc.location_id ? "Close" : "Manage Address"}
                    </button>
                  </>
                )}
              </div>
            </div>
            {expandedId === loc.location_id && (
              <div className="mt-3 grid gap-4 border-t border-slate-100 pt-3 dark:border-white/10 lg:grid-cols-3">
                <div className="grid gap-2 sm:grid-cols-2 lg:col-span-2">
                  <div className="sm:col-span-2">
                    <label className={labelClass}>Address Line 1</label>
                    <input value={addressDraft.street || ""} onChange={(e) => setAddressDraft((d) => ({ ...d, street: e.target.value }))} data-testid={`org-location-street-${loc.location_id}`} className={`mt-1 w-full ${inputClass}`} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelClass}>Address Line 2</label>
                    <input value={addressDraft.line2 || ""} onChange={(e) => setAddressDraft((d) => ({ ...d, line2: e.target.value }))} data-testid={`org-location-line2-${loc.location_id}`} className={`mt-1 w-full ${inputClass}`} />
                  </div>
                  <div>
                    <label className={labelClass}>City</label>
                    <input value={addressDraft.city || ""} onChange={(e) => setAddressDraft((d) => ({ ...d, city: e.target.value }))} data-testid={`org-location-city-${loc.location_id}`} className={`mt-1 w-full ${inputClass}`} />
                  </div>
                  <div>
                    <label className={labelClass}>State</label>
                    {STATES_BY_COUNTRY[addressDraft.country || "US"] ? (
                      <select
                        value={addressDraft.state || ""}
                        onChange={(e) => setAddressDraft((d) => ({ ...d, state: e.target.value }))}
                        data-testid={`org-location-state-${loc.location_id}`}
                        className={`mt-1 w-full ${inputClass}`}
                      >
                        <option value="">Select…</option>
                        {STATES_BY_COUNTRY[addressDraft.country || "US"].map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : (
                      <input value={addressDraft.state || ""} onChange={(e) => setAddressDraft((d) => ({ ...d, state: e.target.value }))} data-testid={`org-location-state-${loc.location_id}`} className={`mt-1 w-full ${inputClass}`} />
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>Zip</label>
                    <input value={addressDraft.zip || ""} onChange={(e) => setAddressDraft((d) => ({ ...d, zip: e.target.value }))} data-testid={`org-location-zip-${loc.location_id}`} className={`mt-1 w-full ${inputClass}`} />
                  </div>
                  <div>
                    <label className={labelClass}>Country</label>
                    <select
                      value={addressDraft.country || "US"}
                      onChange={(e) => setAddressDraft((d) => ({ ...d, country: e.target.value, state: "" }))}
                      data-testid={`org-location-country-${loc.location_id}`}
                      className={`mt-1 w-full ${inputClass}`}
                    >
                      {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2 flex items-center gap-3">
                    <button type="button" onClick={() => verifyLocation(loc.location_id)} disabled={verifyingId === loc.location_id} data-testid={`org-location-verify-${loc.location_id}`} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">
                      {verifyingId === loc.location_id ? "Verifying…" : "Verify Address"}
                    </button>
                    {verifyStatus && verifyStatus.configured !== false && !verifyStatus.verified && <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">Could not verify — check the address and try again</span>}
                  </div>
                </div>
                <LocationPreview
                  address={loc.address || {}}
                  compact
                  title="Map"
                  subtitle=""
                  testId={`org-location-map-${loc.location_id}`}
                  editable={isManager}
                  onIdentifyConfirm={(lat, lng) => identifyLocation(loc.location_id, lat, lng)}
                />
              </div>
            )}
          </div>
        ))}
        {additional.length === 0 && (
          <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-slate-200 p-6 text-center dark:border-white/10" data-testid="org-locations-empty">
            <MapPin size={20} className="text-slate-300 dark:text-slate-600" />
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">No additional locations yet.</p>
          </div>
        )}
      </div>

      {isManager && (
        <div className="mt-4 flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 p-3 dark:border-white/10">
          <div>
            <label className={labelClass}>Location Name</label>
            <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. North Jersey Office" data-testid="org-location-new-name" className={`mt-1 ${inputClass}`} />
          </div>
          <div>
            <label className={labelClass}>Type</label>
            <select value={newType} onChange={(e) => setNewType(e.target.value)} data-testid="org-location-new-type" className={`mt-1 ${inputClass}`}>
              <option value="Branch">Branch</option>
              <option value="Office">Office</option>
              <option value="Service Location">Service Location</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <button type="button" onClick={createLocation} disabled={creating || !newName.trim()} data-testid="org-location-create-btn" className="inline-flex items-center gap-1.5 rounded-xl bg-[#0052FF] px-4 py-2 text-xs font-bold text-white disabled:opacity-50">
            <Plus size={14} /> Add Location
          </button>
        </div>
      )}
    </Panel>
  );
};
